import { Box, Grid, MenuItem, styled, TextField } from "@mui/material";
import { FilterTitle } from "./FilterTitle";
import DateTimeRange from "./DateTimeRange";
import { useEffect, useState } from "react";
import { subDays, subHours, subMinutes, startOfDay, endOfDay } from "date-fns";

export interface IFilterTimeRange {
    label: string,
    getRange: () => {from: number, to: number}
}

const DateFilterContainer = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[200],
    '& > *': {
        backgroundColor: '#ffffff',
        position: 'relative',
        zIndex: 1
    }
}))

const now = () => new Date();

const getRangeFromTo = (fromDate: Date, toDate: Date = now()) => ({
    from: fromDate.getTime(),
    to: toDate.getTime()
});

const getRelativeRange = (subtractFn: (date: Date, amount: number) => Date, amount: number) =>
    getRangeFromTo(subtractFn(now(), amount));

const getStartToEndOfDayRange = (date: Date) =>
    getRangeFromTo(startOfDay(date), endOfDay(date));

const quickRanges: IFilterTimeRange[] = [
    { label: 'Last 5 minutes',  getRange: () => getRelativeRange(subMinutes, 5) },
    { label: 'Last 30 minutes', getRange: () => getRelativeRange(subMinutes, 30) },
    { label: 'Last 1 hour',     getRange: () => getRelativeRange(subHours, 1) },
    { label: 'Last 3 hours',    getRange: () => getRelativeRange(subHours, 3) },
    { label: 'Last 6 hours',    getRange: () => getRelativeRange(subHours, 6) },
    { label: 'Last 12 hours',   getRange: () => getRelativeRange(subHours, 12) },
    { label: 'Last 24 hours',   getRange: () => getRelativeRange(subHours, 24) },
    { label: 'Today',           getRange: () => getStartToEndOfDayRange(now()) },
    { label: 'Yesterday',       getRange: () => getStartToEndOfDayRange(subDays(now(), 1)) },
    { label: 'Last 2 days',     getRange: () => getRelativeRange(subDays, 2) },
    { label: 'Last 7 days',     getRange: () => getRelativeRange(subDays, 7) },
    { label: 'Last 30 days',    getRange: () => getRelativeRange(subDays, 30) },
    { label: 'Last 90 days',    getRange: () => getRelativeRange(subDays, 90) },
    { label: 'Last 180 days',   getRange: () => getRelativeRange(subDays, 180) },
    { label: 'Last year',       getRange: () => getRelativeRange(subDays, 365) },
    { label: 'Last 2 years',    getRange: () => getRelativeRange(subDays, 730) },
    { label: 'Last 5 years',    getRange: () => getRelativeRange(subDays, 1825) },
];

interface IFilterTimeRangeProps {
    title?: string
    setTimeRange?: (range: IFilterTimeRange) => void
    lowerBound: number | undefined,
    upperBound: number | undefined,
    setLowerBound: (num: number) => void,
    setUpperBound: (num: number) => void
}

const FilterTimeRange: React.FC<IFilterTimeRangeProps> = (props) => {

    const [selectedRange, setSelectedRange] = useState<string | undefined>(undefined);

    useEffect(() => {
        if ((props.lowerBound === undefined && props.upperBound === undefined) && selectedRange !== undefined) {
            setSelectedRange(undefined);
        }
    }, [props.lowerBound, props.upperBound, selectedRange]);

    const handleSelectedRange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const selected = quickRanges.find(r => r.label === e.target.value);
        if (selected) {
            const { from, to } = selected.getRange();
            setSelectedRange(e.target.value);
            props.setLowerBound(from)
            props.setUpperBound(to)
            if (props.setTimeRange) props.setTimeRange(selected)
        }
    }
 
    const handleDateChange = (value: number, func: (num: number) => void) => {
        setSelectedRange(undefined);
        func(value)
    }

    return (
        <Grid item xs={12}>
            <FilterTitle> 
                {props.title ?? "Date range"}
            </FilterTitle>
            <DateFilterContainer>
                <TextField
                    select
                    size="small"
                    fullWidth
                    label="Quick Range (GMT)"
                    value={selectedRange ?? ""}
                    onChange={handleSelectedRange}
                    SelectProps={{
                        MenuProps: {
                            PaperProps: { style: { maxHeight: 250 } }
                        }
                    }}
                    sx={{ marginBottom: 2 }}
                >
                    {quickRanges.map(opt => (
                        <MenuItem key={opt.label} value={opt.label}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>
                
                <DateTimeRange
                    from={props.lowerBound}
                    to={props.upperBound}
                    onFromDateChange={value => handleDateChange(value, props.setLowerBound)}
                    onToDateChange={value => handleDateChange(value, props.setUpperBound)}
                />
            </DateFilterContainer>
        </Grid>
    )
}

export default FilterTimeRange